import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { authService } from '@/services/auth';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// Pagina de redefinicao de senha. Suporta 2 modos:
//
// 1) firstAccess (?firstAccess=true): user ja esta autenticado (acabou
//    de logar com senha temporaria). Form simples (nova senha + confirma).
//    POST /auth/first-access-change-password retorna LoginResponse novo.
//
// 2) via email (?token=...): user anonimo chega pelo link do e-mail de
//    redefinicao. Form de nova senha + confirmacao, POST /auth/reset-password
//    (204), e volta pro login.
//
// Sem nenhum dos dois: mensagem de orientacao + botao volta pro login.
export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const isFirstAccess = params.get('firstAccess') === 'true';
  const { user, setSession } = useAuth();
  const navigate = useNavigate();

  // O token sai da URL na montagem e vive so em estado. O caso de SUCESSO ja
  // limparia a URL pelo navigate replace, mas uma tentativa FALHA (senha
  // fraca, confirmacao divergente) deixaria um token valido por ate 60
  // minutos na barra de endereco e no historico do browser.
  const [token] = useState<string | null>(() => params.get('token'));
  useEffect(() => {
    if (!token) return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has('token')) return;
    url.searchParams.delete('token');
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }, [token]);

  const [novaSenha, setNovaSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [erroToken, setErroToken] = useState<string | null>(null);

  // firstAccess sem auth: voltou de algum redirect estranho. Manda pro login.
  if (isFirstAccess && !user) {
    return <Navigate to="/login" replace />;
  }

  // Validacao espelha senhaForteSchema do backend.
  const senhaTrim = novaSenha.trim();
  const senhaErrors: string[] = [];
  if (senhaTrim.length < 8) senhaErrors.push('mínimo 8 caracteres');
  if (!/[A-Za-z]/.test(senhaTrim)) senhaErrors.push('1 letra');
  if (!/[0-9]/.test(senhaTrim)) senhaErrors.push('1 número');
  const senhaOk = senhaErrors.length === 0 && senhaTrim === confirmacao;

  async function handleFirstAccessSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!senhaOk) {
      toast.error('Senha não atende aos requisitos.');
      return;
    }
    setSubmitting(true);
    try {
      const resp = await authService.firstAccessChangePassword(senhaTrim);
      // Backend retorna LoginResponse atualizado (firstAccess=false).
      // Re-aplica sessao pra atualizar contexto auth + token.
      setSession(resp.user, resp.accessToken);
      toast.success('Senha definida! Bem-vindo.');
      navigate('/', { replace: true });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.code === 'ERR_VALIDATION' && err.fields
            ? Object.values(err.fields).join(' ')
            : err.detail
          : 'Erro ao definir senha.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleTokenSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    if (!senhaOk) {
      toast.error('Senha não atende aos requisitos.');
      return;
    }
    setSubmitting(true);
    setErroToken(null);
    try {
      // 204 No Content: sucesso e a ausencia de excecao, nunca resp.ok.
      await authService.resetPassword(token, senhaTrim);
      toast.success('Senha redefinida! Faça login com a nova senha.');
      navigate('/login', { replace: true });
    } catch {
      // Mensagem generica UNICA, sem ramificar por ERR_*: token expirado, ja
      // usado e inexistente nao podem ser distinguiveis pela tela. O submit
      // so habilita com senha valida, entao erro de validacao daqui e
      // praticamente inalcancavel pela UI.
      setErroToken(
        'Não foi possível redefinir a senha com este link. Solicite um novo link de redefinição.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg p-6">
      <div className="max-w-md w-full bg-surface rounded-xl shadow-card border border-border p-8">
        <h1 className="text-2xl font-bold text-text-strong mb-2">
          {isFirstAccess ? 'Definir senha de acesso' : 'Redefinir senha'}
        </h1>

        {isFirstAccess ? (
          <FirstAccessForm
            novaSenha={novaSenha}
            confirmacao={confirmacao}
            senhaErrors={senhaErrors}
            senhaOk={senhaOk}
            submitting={submitting}
            onChangeSenha={setNovaSenha}
            onChangeConfirmacao={setConfirmacao}
            onSubmit={handleFirstAccessSubmit}
            userName={user?.nome ?? null}
          />
        ) : token ? (
          <TokenResetForm
            novaSenha={novaSenha}
            confirmacao={confirmacao}
            senhaErrors={senhaErrors}
            senhaOk={senhaOk}
            submitting={submitting}
            erro={erroToken}
            onChangeSenha={setNovaSenha}
            onChangeConfirmacao={setConfirmacao}
            onSubmit={handleTokenSubmit}
          />
        ) : (
          <>
            <p className="text-sm text-text-muted mb-4">
              Para redefinir sua senha, peça ao administrador um reset ou aguarde o link por email.
            </p>
            <Link
              to="/login"
              className="inline-block mt-2 text-sm text-primary hover:text-primary-hover font-medium"
            >
              Voltar para login
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Form do modo ?token=. Irmao de FirstAccessForm: mesma estrutura e mesmas
 * classes, sem padrao visual novo. O token nunca e renderizado.
 */
function TokenResetForm({
  novaSenha,
  confirmacao,
  senhaErrors,
  senhaOk,
  submitting,
  erro,
  onChangeSenha,
  onChangeConfirmacao,
  onSubmit,
}: {
  novaSenha: string;
  confirmacao: string;
  senhaErrors: string[];
  senhaOk: boolean;
  submitting: boolean;
  erro: string | null;
  onChangeSenha: (v: string) => void;
  onChangeConfirmacao: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
}) {
  const senhaTrim = novaSenha.trim();
  return (
    <>
      <p className="text-sm text-text-muted mb-6">
        Defina uma nova senha para sua conta. Este link é de uso único e expira em 60 minutos.
      </p>
      {erro ? (
        <div
          role="alert"
          data-testid="reset-erro"
          className="text-sm text-danger-text bg-surface-soft border border-border rounded p-3 mb-4"
        >
          {erro}
        </div>
      ) : null}
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <Label htmlFor="rt-senha">Nova senha *</Label>
          <Input
            id="rt-senha"
            type="password"
            autoComplete="new-password"
            value={novaSenha}
            onChange={(e) => onChangeSenha(e.target.value)}
            placeholder="Mínimo 8 caracteres + 1 letra + 1 número"
            autoFocus
          />
          {senhaTrim && senhaErrors.length > 0 ? (
            <p className="text-[11px] text-danger-text mt-1">
              Falta: {senhaErrors.join(', ')}.
            </p>
          ) : null}
        </div>
        <div>
          <Label htmlFor="rt-confirm">Confirmação *</Label>
          <Input
            id="rt-confirm"
            type="password"
            autoComplete="new-password"
            value={confirmacao}
            onChange={(e) => onChangeConfirmacao(e.target.value)}
            placeholder="Digite a senha novamente"
          />
          {confirmacao && senhaTrim !== confirmacao ? (
            <p className="text-[11px] text-danger-text mt-1">As senhas não batem.</p>
          ) : null}
        </div>
        <Button type="submit" variant="primary" className="w-full" disabled={!senhaOk || submitting}>
          {submitting ? 'Salvando...' : 'Redefinir senha'}
        </Button>
      </form>
      <Link
        to="/login"
        className="inline-block mt-6 text-sm text-primary hover:text-primary-hover font-medium"
      >
        Solicitar novo link
      </Link>
    </>
  );
}

function FirstAccessForm({
  novaSenha,
  confirmacao,
  senhaErrors,
  senhaOk,
  submitting,
  onChangeSenha,
  onChangeConfirmacao,
  onSubmit,
  userName,
}: {
  novaSenha: string;
  confirmacao: string;
  senhaErrors: string[];
  senhaOk: boolean;
  submitting: boolean;
  onChangeSenha: (v: string) => void;
  onChangeConfirmacao: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  userName: string | null;
}) {
  const senhaTrim = novaSenha.trim();
  return (
    <>
      <p className="text-sm text-text-muted mb-6">
        {userName ? `Olá, ${userName}. ` : ''}
        Defina uma senha pessoal para usar daqui em diante. Sua senha temporária não será mais
        válida após esta etapa.
      </p>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <Label htmlFor="ra-senha">Nova senha *</Label>
          <Input
            id="ra-senha"
            type="password"
            autoComplete="new-password"
            value={novaSenha}
            onChange={(e) => onChangeSenha(e.target.value)}
            placeholder="Mínimo 8 caracteres + 1 letra + 1 número"
            autoFocus
          />
          {senhaTrim && senhaErrors.length > 0 ? (
            <p className="text-[11px] text-danger-text mt-1">
              Falta: {senhaErrors.join(', ')}.
            </p>
          ) : null}
        </div>
        <div>
          <Label htmlFor="ra-confirm">Confirmação *</Label>
          <Input
            id="ra-confirm"
            type="password"
            autoComplete="new-password"
            value={confirmacao}
            onChange={(e) => onChangeConfirmacao(e.target.value)}
            placeholder="Digite a senha novamente"
          />
          {confirmacao && senhaTrim !== confirmacao ? (
            <p className="text-[11px] text-danger-text mt-1">As senhas não batem.</p>
          ) : null}
        </div>
        <Button type="submit" variant="primary" className="w-full" disabled={!senhaOk || submitting}>
          {submitting ? 'Salvando...' : 'Definir senha e continuar'}
        </Button>
      </form>
    </>
  );
}
